import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useFeatureAccess } from '../../../contexts/FeatureAccessContext';
import { useTutorTenantLabels } from '../../tutor/hooks/useTutorTenantLabels';
import { useLearnerCurriculum } from '../../curriculum/hooks/useLearnerCurriculum';
import { isEffectiveMalayLearner } from '../../curriculum/learnerCurriculumOverride';
import { useStudentMe } from '../../roster/hooks/useStudentMe';
import {
  getMalayFormativeHome,
  type FormativeHomeCard,
} from '../../tutor/api/malayFormativeProgress.client';
import {
  mergeEnabledFormativeHomeCards,
  type MalayFormativeFeatureGate,
} from '../../tutor/malayFormativeHomeCards';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';

export const MALAY_FORMATIVE_HOME_QUERY_KEY = ['student-dashboard', 'malay-formative-home'] as const;

export type MalayFormativeHomeState = {
  /** Whether the hook is active for this tenant/features. */
  enabled: boolean;
  cards: FormativeHomeCard[];
  rawCards: FormativeHomeCard[] | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  features: MalayFormativeFeatureGate;
};

function mapFormativeError(err: unknown): string {
  const status = err instanceof AxiosError ? err.response?.status : undefined;
  const mapped = getApiErrorMessage(err);
  const opaque =
    status === 404 ||
    status === 502 ||
    status === 503 ||
    mapped === 'Something went wrong. Please try again.';
  return opaque ? 'dashboard.malayFormative.loadError' : mapped || 'dashboard.malayFormative.loadError';
}

export function useMalayFormativeHome(active = true): MalayFormativeHomeState {
  const { isSchoolFeatureEnabled, loading: featureLoading } = useFeatureAccess();
  const { labels, loading: labelsLoading } = useTutorTenantLabels();
  const { curriculum } = useLearnerCurriculum();
  const isLearnerMalay = isEffectiveMalayLearner({
    curriculum,
    legacyTenantIsMalay: labels.isMalay,
  });
  const { grade, isRosterLoading } = useStudentMe();

  const aiTutorEnabled = isSchoolFeatureEnabled('ai_tutor');
  const narrativeWritingEnabled = isSchoolFeatureEnabled('narrative_writing');
  const readingComprehensionEnabled = isSchoolFeatureEnabled('reading_comprehension');
  const features = useMemo(
    () => ({
      aiTutorEnabled,
      narrativeWritingEnabled,
      readingComprehensionEnabled,
      grade,
    }),
    [aiTutorEnabled, narrativeWritingEnabled, readingComprehensionEnabled, grade],
  );

  const enabled =
    active &&
    isLearnerMalay &&
    (aiTutorEnabled || narrativeWritingEnabled) &&
    !featureLoading &&
    !labelsLoading &&
    !isRosterLoading;

  const query = useQuery<FormativeHomeCard[], Error>({
    queryKey: MALAY_FORMATIVE_HOME_QUERY_KEY,
    queryFn: getMalayFormativeHome,
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const rawCards = query.data ?? null;
  const cards = useMemo(
    () => mergeEnabledFormativeHomeCards(rawCards, features),
    [rawCards, features],
  );

  return {
    enabled: active && isLearnerMalay && (aiTutorEnabled || narrativeWritingEnabled),
    cards,
    rawCards,
    loading: enabled && query.isLoading,
    error: query.error ? mapFormativeError(query.error) : null,
    reload: () => void query.refetch(),
    features,
  };
}

